import { useMemo, useState, useEffect } from "react";
import ThemeSwitcher from "../components/ThemeSwitcher";
import { getDashboard } from "../store/slices/auth";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";


export const bodyClass =
  "bg-slate-950 text-slate-100 min-h-screen flex flex-col font-sans selection:bg-blue-600 selection:text-white";

const dutyWingOptions = [
  "POLLING_STATION",
  "ROUTE_MOBILE",
  "CHECK_POST",
  "STRIKING_FORCE",
  "FLYING_SQUAD",
  "STATIC_SURVEILLANCE",
  "VIDEO_SURVEILLANCE",
  "COORDINATION_TEAM",
  "GAS_PARTY",
  "OTHER",
];

const getId = (value) =>
  typeof value === "object" ? value?._id || "" : value || "";

const getSensitivityStyle = (value) => {
  switch (String(value || "NORMAL").toUpperCase()) {
    case "HYPER_SENSITIVE":
      return "bg-rose-950 text-rose-400 border-rose-700";

    case "SENSITIVE":
      return "bg-amber-950 text-amber-400 border-amber-700";

    default:
      return "bg-emerald-950 text-emerald-400 border-emerald-700";
  }
};

const getAssignedStaff = (booth) =>
  Array.isArray(booth.assignedStaff)
    ? booth.assignedStaff.filter(
      (staff) => staff.assignmentStatus !== "UNASSIGNED"
    )
    : [];

export default function BoothsMarkup({
  booths = [],
  totalBooths = 0,
  statistics = {},
  loading = false,
  detailsLoading = false,
  createLoading = false,
  updateLoading = false,
  error = null,

  search,
  setSearch,
  sensitivity,
  setSensitivity,
  status,
  setStatus,

  viewMode,
  setViewMode,

  showModal,
  editingBoothId,
  form,
  formError,

  subdivisions = [],
  circles = [],
  stations = [],
  officers = [],

  onAddBooth,
  onEditBooth,
  onCloseModal,
  onFormChange,
  onSubdivisionChange,
  onCircleChange,
  onOfficerToggle,
  onOfficerDutyChange,
  onOfficerGeneralNumberChange,
  onSubmit,
}) {


  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user,
    dashboard,
    dashboardLoading,
    dashboardError
  } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getDashboard());
  }, [dispatch]);

  const handleLogout = () => {
    const confirmed = window.confirm(
      "Are you sure you want to logout?"
    );

    if (!confirmed) return;

    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("admin");

    sessionStorage.clear();

    if (window.adminSocket) {
      window.adminSocket.disconnect();
      window.adminSocket = null;
    }

    window.location.replace("/login");
  };


  const selectedCircles = circles.filter(
    (circle) =>
      String(getId(circle.subDivisionId)) ===
      String(form.subDivisionId)
  );

  const selectedStations = stations.filter(
    (station) =>
      String(getId(station.subDivisionId)) ===
      String(form.subDivisionId) &&
      String(getId(station.circleId)) ===
      String(form.circleId)
  );
  const selectedOfficerIds = (form.assignedStaff || []).map(
    (staff) => staff.officerId
  );

  const selectedOfficers = (form.assignedStaff || []).map(
    (staff) => ({
      ...staff,
      officer:
        officers.find(
          (officer) =>
            String(officer._id) === String(staff.officerId)
        ) ||
        staff.officerDetails ||
        null,
    })
  );

  const isSaving = createLoading || updateLoading;

  const getBoothSubdivision = (booth) =>
    booth.subDivisionId?.name ||
    booth.subDivisionName ||
    "--";

  const getBoothCircle = (booth) =>
    booth.circleId?.name ||
    booth.circleName ||
    "--";

  const getBoothStation = (booth) =>
    booth.policeStationId?.name ||
    booth.policeStationName ||
    "--";

  return (
    <>
      {/* HEADER */}
      <header className="bg-slate-900 border-b border-slate-800 px-4 py-2 flex items-center justify-between shrink-0 shadow-md h-16 relative z-30">
        {/* 1. OFFICIAL IDENTITY & BRANDING */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-700 via-indigo-600 to-blue-500 flex items-center justify-center text-white shadow-lg shadow-blue-900/50 text-xl font-black shrink-0 border border-blue-400/30">
            🇮🇳
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-sm font-black tracking-tight text-white flex items-center gap-2 uppercase">
                Nellore Police

              </h1>
            </div>

          </div>
        </div>
        {/* 2. ICONS-WISE PORTAL NAVIGATION BAR (Proper Website Style) */}
        <nav className="hidden lg:flex items-center bg-slate-950 p-1 rounded-xl border border-slate-800 text-xs font-semibold shadow-inner">
          <a href="/admin" className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition flex items-center gap-1.5">
            <span>
              🗺️
            </span>
            <span>
              Live Map
            </span>
          </a>
          <a href="/emergency-sos" className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition flex items-center gap-1.5">
            <span>
              🚨
            </span>
            <span>
              Incident Grid
            </span>
          </a>


        </nav>
        {/* 3. ACTIONS, TOOLS DROPDOWN LIST, THEME & USER PROFILE */}
        <div className="flex items-center gap-2">
          {/* Register Officer Primary Quick Action */}

          {/* Upload Primary Quick Action */}
          <button onClick={(event) => { openUploadModal('MASTER') }} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg shadow flex items-center gap-1.5 transition">
            <span>
              📤
            </span>
            {' '}
            <span className="hidden sm:inline">
              Upload
            </span>
            {' '}Roster
          </button>
          {/* Operations & Tools Dropdown (Proper List-Wise View) */}
          <div className="relative inline-block text-left" id="header-tools-dropdown-container">


          </div>

          <ThemeSwitcher className="mr-0.5" labelClass="hidden 2xl:inline" />
          <div className="h-6 w-px bg-slate-800 mx-0.5"></div>
          {/* Authenticated Commander Profile & Logout */}
          <div className="flex items-center gap-2 pl-1">
            <div id="header-commander-insignia" className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black shadow border border-yellow-400/40 shrink-0" style={{ background: "#172554", color: "#fef08a" }}>
              🦁
            </div>
            <div className="hidden sm:block text-right">
              <div
                id="header-commander-name"
                className="text-xs font-black text-white leading-tight truncate max-w-[130px]"
              >
                {user?.displayName || user?.name || user?.username || "Admin"}
              </div>

              <div
                id="header-commander-scope"
                className="text-[10px] text-amber-300 font-bold leading-tight truncate max-w-[160px]"
              >
                👑 {user?.designation || user?.role || "Administrator"}
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              title="Logout"
              className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-red-900/40 text-slate-300 hover:text-red-300 border border-slate-700 flex items-center justify-center text-sm transition cursor-pointer"
            >
              🚪
            </button>
          </div>
        </div>
      </header>

      {/* TOP KPI METRIC STRIP (CLICK ANY CARD TO OPEN IN SEPARATE GRID VIEW) */}
      <div className="bg-slate-900/90 border-b border-slate-800/80 px-4 py-2 shrink-0 grid grid-cols-10 gap-2 text-xs">

        {/* Total Sub Divisions */}
        <div
          onClick={() => navigate("/sub-divisions")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-purple-400 transition flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] text-purple-400 uppercase font-bold tracking-wider">
              Sub Divisions
            </div>
            <div className="text-base font-black text-purple-300">
              {dashboardLoading ? "..." : dashboard?.totalSubDivisions ?? 0}
            </div>
          </div>
          <span className="text-xl text-purple-400 opacity-50">🏢</span>
        </div>

        {/* Total Circles */}
        <div
          onClick={() => navigate("/circles")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-sky-400 transition flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] text-sky-400 uppercase font-bold tracking-wider">
              Total Circles
            </div>
            <div className="text-base font-black text-sky-300">
              {dashboardLoading ? "..." : dashboard?.totalCircles ?? 0}
            </div>
          </div>
          <span className="text-xl text-sky-400 opacity-50">⭕</span>
        </div>

        {/* Total Police Stations */}
        <div
          onClick={() => navigate("/stations")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-teal-400 transition flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] text-teal-400 uppercase font-bold tracking-wider">
              Police Stations
            </div>
            <div className="text-base font-black text-teal-300">
              {dashboardLoading ? "..." : dashboard?.totalStations ?? 0}
            </div>
          </div>
          <span className="text-xl text-teal-400 opacity-50">🚔</span>
        </div>
        <div onClick={() => navigate("/booths")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-blue-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
              Total Booths
            </div>
            <div id="stat-total-booths" className="text-base font-black text-white">
              {dashboardLoading ? "..." : dashboard?.totalPollingStations ?? 0}
            </div>
          </div>
          <span className="text-xl opacity-40">
            🗳️
          </span>
        </div>
        <div onClick={() => navigate("/officers")} className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-indigo-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-indigo-400 uppercase font-bold tracking-wider">
              Total Men
            </div>
            <div id="stat-total-staff" className="text-base font-black text-indigo-300">
              {dashboardLoading ? "..." : dashboard?.totalOfficers ?? 0}
            </div>
          </div>
          <span className="text-xl text-indigo-400 opacity-50">
            👮
          </span>
        </div>
        <div onClick={() => navigate("/booths")} className="cursor-pointer bg-rose-950/40 hover:bg-rose-900/40 rounded-lg p-2 border border-rose-600/40 hover:border-rose-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-rose-400 uppercase font-bold tracking-wider">
              Hyper Sensitive
            </div>
            <div id="stat-hyper-sensitive" className="text-base font-black text-rose-400">
              {dashboardLoading ? "..." : dashboard?.hyperSensitiveBooths ?? 0}
            </div>
          </div>
          <span className="text-xl text-rose-400">
            🔥
          </span>
        </div>
        <div onClick={() => navigate("/booths")} className="cursor-pointer bg-amber-950/30 hover:bg-amber-900/40 rounded-lg p-2 border border-amber-600/40 hover:border-amber-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-amber-400 uppercase font-bold tracking-wider">
              Sensitive
            </div>
            <div id="stat-hyper-sensitive" className="text-base font-black text-rose-400">
              {dashboardLoading ? "..." : dashboard?.hyperSensitiveBooths ?? 0}
            </div>
          </div>
          <span className="text-xl text-amber-400">
            ⚠️
          </span>
        </div>

        <div onClick={() => navigate("/staff/on-site")} title="View Verified On-Site Personnel in Grid View" className="cursor-pointer bg-emerald-950/20 hover:bg-emerald-900/30 rounded-lg p-2 border border-emerald-600/30 hover:border-emerald-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-emerald-400 uppercase font-bold tracking-wider">
              On-Site (≤75m)
            </div>
            <div id="stat-onsite" className="text-base font-black text-emerald-400">
              {dashboardLoading ? "..." : dashboard?.atPollingStation ?? 0}
            </div>
          </div>
          <span className="text-xl text-emerald-500 opacity-60">
            ✓
          </span>
        </div>
        <div id="card-kpi-absent" onClick={() => navigate("/staff/absent")} className="cursor-pointer bg-rose-950/30 hover:bg-rose-900/40 rounded-lg p-2 border border-rose-500/50 hover:border-rose-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-rose-400 uppercase font-bold tracking-wider">
              Not Attended
            </div>
            <div id="stat-non-attended" className="text-base font-black text-rose-400">
              {dashboardLoading ? "..." : dashboard?.absentAttendance ?? 0}
            </div>
          </div>
          <span className="text-xl text-rose-500">
            ✕
          </span>
        </div>
        <div id="card-kpi-sos" onClick={() => navigate("/emergency-sos")} title="Click to View Active Emergency SOS Distress Incidents Directly" className="cursor-pointer bg-red-950/30 hover:bg-red-900/50 rounded-lg p-2 border border-red-500/50 hover:border-red-400 transition flex items-center justify-between animate-sos-strobe">
          <div>
            <div className="text-[10px] text-red-400 uppercase font-bold tracking-wider">
              Emergency SOS
            </div>
            <div id="stat-sos" className="text-base font-black text-red-500">
              {dashboardLoading ? "..." : dashboard?.activeSOS ?? 0}
            </div>
          </div>
          <span className="text-xl text-red-500">
            🚨
          </span>
        </div>
      </div >

      {/* PAGE TITLE */}
      <section className="px-5 py-5  border-slate-800 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-blue-400 font-bold mb-1">
                District Administration / Booth Management
              </div>

              <h2 className="text-xl font-black text-white">
                🗳️ All Polling Booths
              </h2>

              <p className="text-xs text-slate-400 mt-1">
                Manage polling booths and assigned officers.
              </p>
            </div>

            <button
              type="button"
              onClick={onAddBooth}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
            >
              <i className="fa-solid fa-plus" />
              Add Booth
            </button>
          </div>
        </div>
      </section>

      {/* STATISTICS */}
      <section className="px-5 py-4 border-slate-800">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-3">
            <p className="text-[10px] uppercase text-slate-400 font-bold">
              Total Booths
            </p>
            <h3 className="text-xl font-black text-white mt-1">
              {statistics.total || 0}
            </h3>
          </div>

          <div className="bg-rose-950/30 border border-rose-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-rose-400 font-bold">
              Hyper Sensitive
            </p>
            <h3 className="text-xl font-black text-rose-400 mt-1">
              {statistics.hyperSensitive || 0}
            </h3>
          </div>

          <div className="bg-amber-950/30 border border-amber-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-amber-400 font-bold">
              Sensitive
            </p>
            <h3 className="text-xl font-black text-amber-400 mt-1">
              {statistics.sensitive || 0}
            </h3>
          </div>

          <div className="bg-emerald-950/30 border border-emerald-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-emerald-400 font-bold">
              Active Booths
            </p>
            <h3 className="text-xl font-black text-emerald-400 mt-1">
              {statistics.active || 0}
            </h3>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="px-5 py-4  border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-2.5 text-slate-500">
              🔍
            </span>

            <input
              type="text"
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Search booth, code, police station..."
              className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-9 pr-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
            />
          </div>

          <select
            value={sensitivity}
            onChange={(event) =>
              setSensitivity(event.target.value)
            }
            className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white outline-none"
          >
            <option value="ALL">All Sensitivity Levels</option>
            <option value="HYPER_SENSITIVE">
              Hyper Sensitive
            </option>
            <option value="SENSITIVE">Sensitive</option>
            <option value="NORMAL">Normal</option>
          </select>

          <select
            value={status}
            onChange={(event) =>
              setStatus(event.target.value)
            }
            className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white outline-none"
          >
            <option value="ALL">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>

          <div className="flex items-center bg-slate-900 border border-slate-700 rounded-xl p-1">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`px-3 py-2 rounded-lg text-xs font-bold ${viewMode === "grid"
                ? "bg-blue-600 text-white"
                : "text-slate-400 hover:text-white"
                }`}
            >
              <i className="fa-solid fa-grip mr-2" />
              Grid
            </button>

            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`px-3 py-2 rounded-lg text-xs font-bold ${viewMode === "list"
                ? "bg-blue-600 text-white"
                : "text-slate-400 hover:text-white"
                }`}
            >
              <i className="fa-solid fa-list mr-2" />
              List
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              setSearch("");
              setSensitivity("ALL");
              setStatus("ALL");
            }}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-xs font-bold text-slate-300"
          >
            Reset Filters
          </button>
        </div>

        <div className="max-w-7xl mx-auto mt-3 text-[10px] text-slate-500">
          Showing{" "}
          <span className="text-white font-bold">
            {booths.length}
          </span>{" "}
          of {totalBooths} polling booths
        </div>
      </section>

      {/* BOOTH CONTENT */}
      <main className="flex-1 p-5 bg-slate-950">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20 text-slate-400 text-sm">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
              Loading polling booths...
            </div>
          ) : error ? (
            <div className="text-center py-16 bg-red-950/20 border border-red-800/40 rounded-2xl">
              <p className="text-red-400 text-sm font-bold">
                Failed to load polling booths
              </p>
              <p className="text-xs text-slate-400 mt-2">
                {error}
              </p>
            </div>
          ) : booths.length === 0 ? (
            <div className="text-center py-20 bg-slate-900 border border-slate-800 rounded-2xl">
              <div className="text-4xl mb-3">🔍</div>
              <h3 className="text-sm font-bold text-white">
                No Polling Booths Found
              </h3>
              <p className="text-xs text-slate-500 mt-2">
                Try changing your search or filters.
              </p>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {booths.map((booth) => {
                const assignedStaff = getAssignedStaff(booth);

                return (
                  <div
                    key={booth._id}
                    className="bg-slate-900 border border-slate-800 rounded-2xl p-4 hover:border-blue-500/60 transition shadow-lg"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-sm font-black text-white">
                          {booth.name || "Unnamed Booth"}
                        </h3>

                        <p className="text-[11px] text-blue-400 font-bold mt-1">
                          {booth.boothId || booth.code || "--"}
                        </p>
                      </div>

                      <span
                        className={`text-[9px] px-2 py-1 rounded-lg border font-black ${booth.active
                          ? "bg-emerald-950 text-emerald-400 border-emerald-800"
                          : "bg-red-950 text-red-400 border-red-800"
                          }`}
                      >
                        {booth.active ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </div>

                    <div className="mt-3">
                      <span
                        className={`inline-block text-[9px] px-2 py-1 rounded-lg border font-bold ${getSensitivityStyle(
                          booth.sensitivity
                        )}`}
                      >
                        {String(
                          booth.sensitivity || "NORMAL"
                        ).replaceAll("_", " ")}
                      </span>
                    </div>

                    <div className="mt-4 space-y-3 text-xs">
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">
                          Sub-Division
                        </span>
                        <span className="text-slate-200 text-right">
                          {getBoothSubdivision(booth)}
                        </span>
                      </div>

                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">
                          Circle
                        </span>
                        <span className="text-slate-200 text-right">
                          {getBoothCircle(booth)}
                        </span>
                      </div>

                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">
                          Police Station
                        </span>
                        <span className="text-slate-200 font-semibold text-right">
                          {getBoothStation(booth)}
                        </span>
                      </div>

                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">
                          Voters Count
                        </span>
                        <span className="text-white font-bold">
                          {booth.votersCount ?? 0}
                        </span>
                      </div>

                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">
                          GPS Coordinates
                        </span>
                        <span className="text-cyan-400 font-mono text-[10px]">
                          {booth.lat ?? "--"}, {booth.lng ?? "--"}
                        </span>
                      </div>

                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">
                          Geofence Radius
                        </span>
                        <span className="text-slate-200">
                          {booth.geofenceRadius
                            ? `${booth.geofenceRadius} m`
                            : "--"}
                        </span>
                      </div>
                    </div>

                    {/* ASSIGNED OFFICERS */}
                    <div className="mt-4 pt-3 border-t border-slate-800">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-[11px] font-black text-white">
                          👮 Assigned Officers
                        </h4>

                        <span className="text-[10px] px-2 py-1 rounded-lg bg-indigo-950 text-indigo-400 font-bold">
                          {assignedStaff.length} Officers
                        </span>
                      </div>

                      {assignedStaff.length === 0 ? (
                        <p className="text-[10px] text-slate-500 py-2">
                          No officers assigned to this booth.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {assignedStaff.map((staff, index) => {
                            const officer =
                              typeof staff.officerId === "object"
                                ? staff.officerId
                                : null;

                            return (
                              <div
                                key={
                                  officer?._id ||
                                  staff.officerCode ||
                                  index
                                }
                                className="bg-slate-950 border border-slate-800 rounded-xl p-3"
                              >
                                <div className="flex justify-between items-start gap-2">
                                  <div>
                                    <p className="text-[11px] font-bold text-white">
                                      {officer?.displayName ||
                                        staff.name ||
                                        "Unknown Officer"}
                                    </p>

                                    <p className="text-[9px] text-slate-500 mt-1">
                                      {officer?.officerId ||
                                        staff.officerCode ||
                                        "--"}
                                    </p>
                                  </div>

                                  <span className="text-[9px] text-cyan-400 font-bold text-right">
                                    {staff.dutyWing || "--"}
                                  </span>
                                </div>

                                <div className="flex justify-between gap-2 mt-2 text-[9px] text-slate-400">
                                  <span>
                                    {officer?.designation ||
                                      staff.designation ||
                                      "--"}
                                  </span>

                                  <span>
                                    📞{" "}
                                    {officer?.phone ||
                                      staff.phone ||
                                      "--"}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-800 flex justify-end">
                      <button
                        type="button"
                        onClick={() => onEditBooth(booth)}
                        className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
                      >
                        <i className="fa-solid fa-pen-to-square mr-2" />
                        Edit Booth
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* LIST VIEW */
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-x-auto">
              <table className="w-full min-w-[1050px] text-left">
                <thead className="bg-slate-950 text-[10px] uppercase text-slate-400">
                  <tr>
                    <th className="px-4 py-4">Booth</th>
                    <th className="px-4 py-4">Sub-Division</th>
                    <th className="px-4 py-4">Circle</th>
                    <th className="px-4 py-4">Police Station</th>
                    <th className="px-4 py-4">Sensitivity</th>
                    <th className="px-4 py-4">Officers</th>
                    <th className="px-4 py-4">Voters</th>
                    <th className="px-4 py-4">Status</th>
                    <th className="px-4 py-4 text-right">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-800">
                  {booths.map((booth) => (
                    <tr
                      key={booth._id}
                      className="hover:bg-slate-800/50"
                    >
                      <td className="px-4 py-4">
                        <p className="text-xs font-bold text-white">
                          {booth.name}
                        </p>
                        <p className="text-[10px] text-blue-400 mt-1">
                          {booth.boothId || booth.code}
                        </p>
                      </td>

                      <td className="px-4 py-4 text-xs text-slate-300">
                        {getBoothSubdivision(booth)}
                      </td>

                      <td className="px-4 py-4 text-xs text-slate-300">
                        {getBoothCircle(booth)}
                      </td>

                      <td className="px-4 py-4 text-xs text-slate-300">
                        {getBoothStation(booth)}
                      </td>

                      <td className="px-4 py-4">
                        <span
                          className={`text-[9px] px-2 py-1 rounded-lg border font-bold ${getSensitivityStyle(
                            booth.sensitivity
                          )}`}
                        >
                          {String(
                            booth.sensitivity || "NORMAL"
                          ).replaceAll("_", " ")}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-xs text-slate-300">
                        {getAssignedStaff(booth).length}
                      </td>

                      <td className="px-4 py-4 text-xs text-white font-bold">
                        {booth.votersCount ?? 0}
                      </td>

                      <td className="px-4 py-4">
                        <span
                          className={`text-[9px] px-2 py-1 rounded-lg border font-bold ${booth.active
                            ? "bg-emerald-950 text-emerald-400 border-emerald-800"
                            : "bg-red-950 text-red-400 border-red-800"
                            }`}
                        >
                          {booth.active ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => onEditBooth(booth)}
                          className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold"
                        >
                          <i className="fa-solid fa-pen-to-square mr-1" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* FOOTER */}
      <footer className="px-5 py-3 border-t border-slate-800 bg-slate-900 text-center">
        <p className="text-[10px] text-slate-500">
          Nellore Police • Polling Booth Administration
        </p>
      </footer>

      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5">
          <div className="w-full max-w-4xl max-h-[95vh] overflow-y-auto bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl">
            {/* MODAL HEADER */}
            <div className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800 px-5 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-base font-black text-white">
                  {editingBoothId ? "Edit Polling Booth" : "Add Polling Booth"}
                </h3>
                <p className="text-[10px] text-slate-400 mt-1">
                  Enter booth details and assign multiple officers.
                </p>
              </div>

              <button
                type="button"
                onClick={onCloseModal}
                disabled={isSaving}
                className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300"
              >
                ✕
              </button>
            </div>

            {detailsLoading ? (
              <div className="py-20 text-center text-sm text-slate-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
                Loading booth details...
              </div>
            ) : (
              <form onSubmit={onSubmit} className="p-5 space-y-6">
                {formError && (
                  <div className="bg-red-950/40 border border-red-800 rounded-xl p-3 text-xs text-red-300">
                    {formError}
                  </div>
                )}

                {/* BASIC DETAILS */}
                <section>
                  <h4 className="text-xs font-black uppercase tracking-wider text-blue-400 mb-3">
                    Booth Details
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        Booth ID *
                      </label>
                      <input
                        required
                        value={form.boothId}
                        onChange={(event) =>
                          onFormChange("boothId", event.target.value)
                        }
                        placeholder="BTH001"
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        Booth Code *
                      </label>
                      <input
                        required
                        value={form.code}
                        onChange={(event) =>
                          onFormChange("code", event.target.value)
                        }
                        placeholder="001"
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-[10px] text-slate-400 mb-1">
                        Booth Name *
                      </label>
                      <input
                        required
                        value={form.name}
                        onChange={(event) =>
                          onFormChange("name", event.target.value)
                        }
                        placeholder="Government School Booth 1"
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                </section>

                {/* HIERARCHY */}
                <section>
                  <h4 className="text-xs font-black uppercase tracking-wider text-blue-400 mb-3">
                    Location Hierarchy
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        Sub-Division *
                      </label>

                      <select
                        required
                        value={form.subDivisionId}
                        onChange={(event) =>
                          onSubdivisionChange(event.target.value)
                        }
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                      >
                        <option value="">Select Sub-Division</option>

                        {subdivisions.map((item) => (
                          <option key={item._id} value={item._id}>
                            {item.name} ({item.subDivisionId})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        Circle *
                      </label>

                      <select
                        required
                        disabled={!form.subDivisionId}
                        value={form.circleId}
                        onChange={(event) =>
                          onCircleChange(event.target.value)
                        }
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500 disabled:opacity-40"
                      >
                        <option value="">Select Circle</option>

                        {selectedCircles.map((item) => (
                          <option key={item._id} value={item._id}>
                            {item.name} ({item.circleId})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        Police Station *
                      </label>

                      <select
                        required
                        disabled={!form.circleId}
                        value={form.policeStationId}
                        onChange={(event) =>
                          onFormChange(
                            "policeStationId",
                            event.target.value
                          )
                        }
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500 disabled:opacity-40"
                      >
                        <option value="">Select Police Station</option>

                        {selectedStations.map((item) => (
                          <option key={item._id} value={item._id}>
                            {item.name} ({item.stationId})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </section>

                {/* LOCATION */}
                <section>
                  <h4 className="text-xs font-black uppercase tracking-wider text-blue-400 mb-3">
                    GPS and Booth Settings
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        Latitude *
                      </label>
                      <input
                        required
                        type="number"
                        step="any"
                        value={form.lat}
                        onChange={(event) =>
                          onFormChange("lat", event.target.value)
                        }
                        placeholder="13.0827"
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        Longitude *
                      </label>
                      <input
                        required
                        type="number"
                        step="any"
                        value={form.lng}
                        onChange={(event) =>
                          onFormChange("lng", event.target.value)
                        }
                        placeholder="80.2707"
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        Voters Count
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={form.votersCount}
                        onChange={(event) =>
                          onFormChange(
                            "votersCount",
                            event.target.value
                          )
                        }
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        Geofence Radius (m)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={form.geofenceRadius}
                        onChange={(event) =>
                          onFormChange(
                            "geofenceRadius",
                            event.target.value
                          )
                        }
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        Sensitivity
                      </label>
                      <select
                        value={form.sensitivity}
                        onChange={(event) =>
                          onFormChange(
                            "sensitivity",
                            event.target.value
                          )
                        }
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                      >
                        <option value="NORMAL">Normal</option>
                        <option value="SENSITIVE">Sensitive</option>
                        <option value="HYPER_SENSITIVE">
                          Hyper Sensitive
                        </option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-400 mb-1">
                        Status
                      </label>
                      <select
                        value={String(form.active)}
                        onChange={(event) =>
                          onFormChange(
                            "active",
                            event.target.value === "true"
                          )
                        }
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-blue-500"
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                  </div>
                </section>

                {/* MULTIPLE OFFICERS */}
                <section>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider text-blue-400">
                        Assigned Officers
                      </h4>
                      <p className="text-[10px] text-slate-500 mt-1">
                        Select multiple officers and set each officer's duty wing.
                      </p>
                    </div>

                    <span className="text-[10px] px-3 py-1.5 rounded-lg bg-indigo-950 text-indigo-400 font-bold">
                      {selectedOfficerIds.length} Selected
                    </span>
                  </div>

                  <div className="bg-slate-950 border border-slate-800 rounded-xl p-3 max-h-64 overflow-y-auto">
                    {officers.length === 0 ? (
                      <p className="text-xs text-slate-500 text-center py-5">
                        No officers available.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {officers.map((officer) => {
                          const checked = selectedOfficerIds.includes(
                            officer._id
                          );

                          return (
                            <label
                              key={officer._id}
                              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${checked
                                ? "bg-blue-950/40 border-blue-700"
                                : "bg-slate-900 border-slate-800 hover:border-slate-600"
                                }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(event) =>
                                  onOfficerToggle(
                                    officer._id,
                                    event.target.checked
                                  )
                                }
                                className="mt-1 accent-blue-600"
                              />

                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-bold text-white">
                                  {officer.displayName ||
                                    officer.username ||
                                    "Unknown Officer"}
                                </p>

                                <p className="text-[10px] text-slate-400 mt-1">
                                  {officer.officerId || "--"} ·{" "}
                                  {officer.designationName ||
                                    officer.designation ||
                                    "--"}
                                </p>

                                <p className="text-[10px] text-slate-500 mt-1">
                                  {officer.phone || "--"}
                                </p>
                              </div>

                              {checked && (
                                <span className="text-[9px] text-blue-400 font-bold">
                                  SELECTED
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* SELECTED OFFICER DUTY WINGS */}
                  {selectedOfficers.length > 0 && (
                    <div className="mt-4 space-y-3">
                      <h5 className="text-[10px] uppercase font-bold text-slate-400">
                        Configure Selected Officers
                      </h5>

                      {selectedOfficers.map((staff) => (
                        <div
                          key={staff.officerId}
                          className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-950 border border-slate-800 rounded-xl p-3"
                        >
                          <div className="md:col-span-1">
                            <p className="text-xs font-bold text-white">
                              {staff.officer?.displayName ||
                                staff.officer?.username ||
                                staff.officer?.officerId ||
                                staff.officerId}
                            </p>

                            <p className="text-[10px] text-slate-500 mt-1">
                              {staff.officer?.officerId || "--"}
                            </p>
                          </div>

                          <div>
                            <label className="block text-[9px] text-slate-400 mb-1">
                              Duty Wing
                            </label>

                            <select
                              value={staff.dutyWing}
                              onChange={(event) =>
                                onOfficerDutyChange(
                                  staff.officerId,
                                  event.target.value
                                )
                              }
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-[10px] text-white outline-none focus:border-blue-500"
                            >
                              {dutyWingOptions.map((wing) => (
                                <option key={wing} value={wing}>
                                  {wing.replaceAll("_", " ")}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-[9px] text-slate-400 mb-1">
                              General Number
                            </label>

                            <input
                              value={staff.generalNumber || ""}
                              onChange={(event) =>
                                onOfficerGeneralNumberChange(
                                  staff.officerId,
                                  event.target.value
                                )
                              }
                              placeholder="Optional"
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-2 text-[10px] text-white outline-none focus:border-blue-500"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                {/* MODAL FOOTER */}
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-slate-800">
                  <button
                    type="button"
                    onClick={onCloseModal}
                    disabled={isSaving}
                    className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-slate-300 disabled:opacity-50"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-xs font-bold text-white disabled:opacity-50"
                  >
                    {isSaving
                      ? "Saving..."
                      : editingBoothId
                        ? "Update Booth"
                        : "Create Booth"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}